/**
 * What the engine knows about Entries it does not hold: the catalogue index across every shipped
 * Pack (for ChoiceSet Pack queries, as Foundry queries its compendium index) and the Snapshot's
 * config tables and rule strings. Optional input to `deriveSheet`; without it, Pack queries and
 * config lookups report themselves as unavailable instead of failing.
 */
import type { IndexRecord, SnapshotConfig, SpellcastingTables } from "../../shared/snapshot-format";
import { FEATURE_CATEGORIES } from "./values";

export interface Catalogue {
  readonly records: readonly IndexRecord[];
  byUuid(uuid: string): IndexRecord | undefined;
  /** A `CONFIG.PF2E` table by name: value -> label. */
  table(name: string): Record<string, string> | undefined;
  /** English text for a localisation key, or the key itself when unknown. */
  localize(key: string): string;
  /** The class slot tables and domain spells read from Foundry's journals at build time (#10); null without config. */
  readonly spellcasting: SpellcastingTables | null;
}

export function createCatalogue(records: readonly IndexRecord[], config?: Partial<SnapshotConfig>): Catalogue {
  let map: Map<string, IndexRecord> | undefined;
  const tables = config?.tables ?? {};
  const strings = config?.strings ?? {};
  return {
    records,
    spellcasting: config?.spellcasting ?? null,
    byUuid(uuid) {
      map ??= new Map(records.map((r) => [r.u, r]));
      return map.get(uuid);
    },
    table(name) {
      return tables[name];
    },
    localize(key) {
      return strings[key] ?? key;
    },
  };
}

const MAGIC_TRADITIONS = ["arcane", "divine", "occult", "primal"];

/** The document type an index record's roll options name: "feature" for feats of a feature category. */
export function recordItemType(record: IndexRecord): string {
  return record.t === "feat" && (FEATURE_CATEGORIES as readonly string[]).includes(record.c ?? "") ? "feature" : record.t;
}

/**
 * The roll options Foundry builds for an item constructed from index data (`Item#getRollOptions`
 * over the fields the ChoiceSet query requests), so a Pack `filter` predicate tests the same
 * statements here as upstream.
 */
export function recordRollOptions(record: IndexRecord, prefix = "item"): string[] {
  const itemType = recordItemType(record);
  const isFeature = itemType === "feature";
  const id = record.u.slice(record.u.lastIndexOf(".") + 1);
  const options = [`${prefix}:id:${id}`, `${prefix}:${record.s}`, `${prefix}:slug:${record.s}`];
  for (const tag of record.tg ?? []) options.push(`${prefix}:tag:${tag}`);
  for (const trait of record.tr) options.push(`${prefix}:trait:${trait}`);
  if (record.r && !(record.t === "feat" && isFeature)) options.push(`${prefix}:rarity:${record.r}`);
  if (record.t === "spell" || record.mg || record.tr.some((t) => t === "magical" || MAGIC_TRADITIONS.includes(t))) options.push(`${prefix}:magical`);
  if (typeof record.l === "number" && !(record.t === "feat" && record.l === 0)) options.push(`${prefix}:level:${record.l}`);
  if (prefix !== itemType) options.unshift(`${prefix}:type:${itemType}`);
  if (record.c && (record.t === "feat" || record.t === "deity" || record.t === "weapon" || record.t === "action")) options.push(`${prefix}:category:${record.c}`);
  if (record.t === "heritage") options.push(record.a ? `${prefix}:ancestry:${record.a}` : `${prefix}:versatile`);
  if (record.t === "weapon") {
    options.push(`${prefix}:group:${record.g ?? "none"}`);
    if (record.b) options.push(`${prefix}:base:${record.b}`);
    if (record.dt) options.push(`${prefix}:damage:type:${record.dt}`);
    options.push(record.rg === null || record.rg === undefined ? `${prefix}:melee` : `${prefix}:ranged`);
    if (typeof record.rg === "number") options.push(`${prefix}:range-increment:${record.rg}`);
  }
  if (record.t === "shield" && record.b) options.push(`${prefix}:base:${record.b}`);
  return options;
}

/** Foundry's `maxTakable` for a feat record: absent means once, 0 means unlimited. */
export function recordMaxTakable(record: IndexRecord): number {
  if (record.t !== "feat") return Infinity;
  if (record.mt === undefined) return 1;
  return record.mt === 0 ? Infinity : record.mt;
}
